import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Pagination,
  Box,
} from "@mui/material";
import axios from "axios";
import "../../services/GlobalVariables";
import Navbar from "../setup/Navbar";

const ItemsQuickUpdate = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${global.SET_API_URL}items/getAllItems`);
      setItems(response.data || []);
      // Initialize edit values with current item data
      const initialEditValues = {};
      (response.data || []).forEach(item => {
        initialEditValues[item.itemCode] = {
          itemDesc: item.itemDesc || "",
          salesPrice: item.salesPrice || 0,
          categoryCode: item.categoryCode || "",
          cateDescription: item.cateDescription || "",
          makingTime: item.makingTime || 0,
        };
      });
      setEditValues(initialEditValues);
    } catch (error) {
      console.error("Error fetching items:", error);
      setMessage("Error fetching items");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${global.SET_API_URL}itemCategory/getAllCategory`);
      setCategories(response.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleInputChange = (itemCode, field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [itemCode]: {
        ...prev[itemCode],
        [field]: value,
      },
    }));
  };

  const handleCategoryChange = (itemCode, cateCode, cateDescription) => {
    setEditValues((prev) => ({
      ...prev,
      [itemCode]: {
        ...prev[itemCode],
        categoryCode: cateCode,
        cateDescription: cateDescription,
      },
    }));
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      let successCount = 0;
      let failCount = 0;

      // Save all modified items
      for (const itemCode in editValues) {
        const itemData = editValues[itemCode];
        const originalItem = items.find(i => i.itemCode === parseInt(itemCode));

        // Check if any field was changed
        const isModified = 
          itemData.itemDesc !== originalItem?.itemDesc ||
          itemData.salesPrice !== originalItem?.salesPrice ||
          itemData.categoryCode !== originalItem?.categoryCode ||
          itemData.makingTime !== originalItem?.makingTime;

        if (isModified) {
          try {
            const updateData = {
              itemCode: parseInt(itemCode),
              itemDesc: itemData.itemDesc,
              salesPrice: parseFloat(itemData.salesPrice),
              categoryCode: parseInt(itemData.categoryCode),
              makingTime: parseInt(itemData.makingTime),
            };

            await axios.post(
              `${global.SET_API_URL}items/updateItem`,
              updateData
            );
            successCount++;
          } catch (error) {
            console.error(`Error updating item ${itemCode}:`, error);
            failCount++;
          }
        }
      }

      if (successCount > 0) {
        setMessage(`${successCount} item(s) updated successfully!${failCount > 0 ? ` (${failCount} failed)` : ''}`);
        setMessageType("success");
        fetchItems();
      } else if (failCount > 0) {
        setMessage(`${failCount} item(s) failed to update`);
        setMessageType("error");
      } else {
        setMessage("No changes to save");
        setMessageType("info");
      }
    } catch (error) {
      console.error("Save error:", error);
      setMessage("Error saving items");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Calculate pagination
  const totalPages = Math.ceil(items.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <Navbar />
      <div style={{ padding: "20px" }}>
        <h2>Quick Item Update</h2>
        <p style={{ color: "#666", marginBottom: "20px" }}>
          Edit items and click "Save All" to update multiple items at once
        </p>

        {message && (
          <Alert
            severity={messageType}
            onClose={() => setMessage("")}
            style={{ marginBottom: "20px" }}
          >
            {message}
          </Alert>
        )}

        {loading ? <p>Loading items...</p> : null}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <div>
            <strong>Total Items: {items.length}</strong> | Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, items.length)} of {items.length}
          </div>
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="contained"
              size="large"
              onClick={handleSaveAll}
              disabled={saving || loading}
              style={{ backgroundColor: "#780202", color: "white", padding: "10px 40px" }}
            >
              {saving ? "Saving..." : "Save All Changes"}
            </Button>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: "#780202" }}>
                <TableCell style={{ color: "white", fontWeight: "bold", width: "80px" }}>
                  Item Code
                </TableCell>
                <TableCell style={{ color: "white", fontWeight: "bold", minWidth: "200px" }}>
                  Description
                </TableCell>
                <TableCell
                  align="right"
                  style={{ color: "white", fontWeight: "bold", width: "120px" }}
                >
                  Sales Price
                </TableCell>
                <TableCell
                  style={{ color: "white", fontWeight: "bold", minWidth: "180px" }}
                >
                  Category
                </TableCell>
                <TableCell
                  align="center"
                  style={{ color: "white", fontWeight: "bold", width: "100px" }}
                >
                  Making Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItems.map((item) => (
                <TableRow key={item.itemCode} hover>
                  <TableCell>{item.itemCode}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={editValues[item.itemCode]?.itemDesc || ""}
                      onChange={(e) =>
                        handleInputChange(item.itemCode, "itemDesc", e.target.value)
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ step: "0.01", min: "0" }}
                      value={editValues[item.itemCode]?.salesPrice || 0}
                      onChange={(e) =>
                        handleInputChange(item.itemCode, "salesPrice", e.target.value)
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={editValues[item.itemCode]?.categoryCode || ""}
                        onChange={(e) => {
                          const selectedCategory = categories.find(c => c.cateCode === e.target.value);
                          handleCategoryChange(
                            item.itemCode,
                            e.target.value,
                            selectedCategory?.cateDescription || ""
                          );
                        }}
                        variant="outlined"
                      >
                        <MenuItem value="">Select Category</MenuItem>
                        {categories.map((cat) => (
                          <MenuItem key={cat.cateCode} value={cat.cateCode}>
                            {cat.cateDescription}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: "0" }}
                      value={editValues[item.itemCode]?.makingTime || 0}
                      onChange={(e) =>
                        handleInputChange(item.itemCode, "makingTime", e.target.value)
                      }
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />
          <Button
            variant="contained"
            size="large"
            onClick={handleSaveAll}
            disabled={saving || loading}
            style={{ backgroundColor: "#780202", color: "white", padding: "10px 40px" }}
          >
            {saving ? "Saving..." : "Save All Changes"}
          </Button>
        </Box>
      </div>
    </>
  );
};

export default ItemsQuickUpdate;
